// The projects kept in this browser, and the About box. GPL-3.0-or-later.
import { newProject, PROJECT_VERSION } from "@dipfield/project";
import { t, tn, dateLocale } from "../i18n/index.js";
import { esc } from "../lib/dom.js";
import { toast } from "../lib/toast.js";
import * as db from "../state/persist.js";
import { load, project, rename } from "../state/store.js";
import { APP, ENGINES } from "../state/engines.js";

function dialog(id: string, title: string, body: string): HTMLDialogElement {
  document.getElementById(id)?.remove();
  const d = document.createElement("dialog"); d.className = "dlg"; d.id = id;
  d.innerHTML = `<div class="dlg-head"><h2>${title}</h2><button class="btn ghost ico" data-close aria-label="${t("Close")}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18"/></svg></button></div><div class="dlg-body">${body}</div>`;
  d.addEventListener("click", (e) => { if ((e.target as HTMLElement).closest("[data-close]") || e.target === d) d.close(); });
  d.addEventListener("close", () => d.remove());
  document.body.appendChild(d); d.showModal();
  return d;
}
const when = (iso: string) => { try { return new Date(iso).toLocaleString(dateLocale(), { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export function newProjectNow() {
  load(newProject({ name: t("Untitled project") }));
  toast(t("New project"));
}

export async function showProjects() {
  const list = await db.listProjects(), cur = project().meta.id, stored = await db.available();
  const d = dialog("dlg-projects", t("Projects"), `
    <label class="field" style="margin-top:0"><span>${t("Name of the open project")}</span><input type="text" id="pj-name" value="${esc(project().meta.name)}" maxlength="120"></label>
    <div class="row"><button class="btn" id="pj-new">${t("New project")}</button></div>
    <p class="muted small">${stored ? t("Projects are kept in this browser on this device. Save a project file to move it or back it up.") : t("This browser does not keep projects (private window?): save a project file before closing.")}</p>
    <ul class="plist">${list.map((e) => `<li class="${e.id === cur ? "cur" : ""}" data-id="${esc(e.id)}"><span class="pn"><b>${esc(e.name)}</b>
      <span>${when(e.savedAt)} · ${tn(e.planes, "{n} row", "{n} rows")}, ${tn(e.sets, "{n} set", "{n} sets")}</span></span>
      ${e.id === cur ? `<span class="chip">${t("open")}</span>` : `<button class="btn small" data-open>${t("Open")}</button>`}
      <button class="icon" data-del aria-label="${t("Delete {name}", { name: esc(e.name) })}" title="${t("Delete project")}">×</button></li>`).join("")}</ul>`);
  d.querySelector("#pj-name")!.addEventListener("change", (e) => rename((e.target as HTMLInputElement).value));
  d.querySelector("#pj-new")!.addEventListener("click", () => { d.close(); newProjectNow(); });
  d.querySelector(".plist")!.addEventListener("click", async (e) => {
    const li = (e.target as HTMLElement).closest<HTMLElement>("li[data-id]"); if (!li) return;
    const id = li.dataset.id!;
    if ((e.target as HTMLElement).closest("[data-open]")) {
      const p = await db.loadProject(id); d.close();
      if (p) { load(p, { save: false }); toast(t("Opened {name}", { name: esc(p.meta.name) })); }
      return;
    }
    if ((e.target as HTMLElement).closest("[data-del]")) {
      const p = await db.loadProject(id); if (!p) return;
      await db.deleteProject(id); li.remove();
      if (id === cur) newProjectNow();
      toast(t("Deleted {name}", { name: esc(p.meta.name) }), async () => { await db.saveProject(p); });
    }
  });
}

export function showAbout() {
  const rows = Object.entries(ENGINES).map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join("");
  dialog("dlg-about", "DipField Suite", `<div class="prose">
    <p>${t("Discontinuity data, joint sets and failure-mode assessment for rock slopes and tunnels.")}</p>
    <dl class="kv"><dt>${t("App")}</dt><dd>${esc(APP.version)}</dd><dt>${t("Project file")}</dt><dd>${t("version {v}", { v: PROJECT_VERSION })}</dd>${rows}</dl>
    <p>${t("Every saved project and export records these versions, so a result can be traced to the engine that produced it.")}</p>
    <p>${t("Free software under the GNU General Public License, version 3 or later. Fonts: Barlow and Anuphan (SIL Open Font License); 3D views: three.js (MIT).")} <a href="./THIRD-PARTY-NOTICES.txt" target="_blank" rel="noopener">${t("Third-party notices")}</a></p></div>`);
}
